import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setMobileCatsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/article/usfans-spreadsheet-guide', label: t('nav.shoppingGuide') },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-background-200 bg-background-50 shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg" />
          </span>
          <span className="font-heading text-base md:text-lg font-bold text-foreground-950 whitespace-nowrap">
            {t('nav.brand')}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === link.to
                  ? 'text-primary-700 bg-primary-50'
                  : 'text-foreground-800 hover:bg-background-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/#categories"
            className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('nav.categories')}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-800 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden max-h-[75vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-3">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}

            <button
              type="button"
              onClick={() => setMobileCatsOpen((v) => !v)}
              className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              <span>{t('nav.categories')}</span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCatsOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileCatsOpen && (
              <div className="mt-1 grid grid-cols-2 gap-1.5 rounded-md bg-background-100 p-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 rounded-md px-2.5 py-2 text-xs font-medium text-foreground-700 hover:bg-background-50 cursor-pointer whitespace-nowrap"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-50 text-secondary-700">
                      <i className={`${cat.icon} text-xs`} />
                    </span>
                    <span>{t(cat.nameKey)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}